import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, ExternalLink, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PlatformIcon } from "@/components/PlatformTabs";

const STATUS_LABEL = {
  pending: "Pending",
  ongoing: "Ongoing",
  completed: "Completed",
  submitted: "Submitted for Approval",
};

const STATUS_CLASS = {
  pending: "badge-pending",
  ongoing: "badge-ongoing",
  completed: "badge-completed",
  submitted: "badge-submitted",
};

export default function LinkCard({ link, platform, onEdit, onDeleted, onStatusChanged, index = 0 }) {
  const [status, setStatus] = useState(link.status);
  const [busy, setBusy] = useState(false);

  const short = (u) => {
    try { return new URL(u).hostname.replace(/^www\./, "") + new URL(u).pathname; }
    catch { return u; }
  };

  const changeStatus = async (v) => {
    setBusy(true);
    setStatus(v);
    try {
      await api.patch(`/links/${link.id}/status`, { status: v });
      toast.success(`Status → ${STATUS_LABEL[v]}`);
      onStatusChanged(link.id, v);
    } catch (err) {
      toast.error(formatApiError(err));
      setStatus(link.status);
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await api.delete(`/links/${link.id}`);
      toast.success("Link removed");
      onDeleted(link.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(link.url);
    toast.success("URL copied");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.5), duration: 0.32 }}
      className="card-tactical chamfered p-5 relative group"
      data-testid={`link-card-${link.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span style={{ color: platform?.color || "#F59E0B" }}>
            <PlatformIcon icon={platform?.icon || "star"} className="w-4 h-4" />
          </span>
          <span className="label-mono">{platform?.name || "—"}</span>
        </div>
        <span
          className={`text-[10px] px-2 py-1 rounded font-display font-bold uppercase tracking-wider ${STATUS_CLASS[status]}`}
        >
          {STATUS_LABEL[status]}
        </span>
      </div>

      <div className="mb-3">
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="font-mono-vrax text-sm text-slate-200 hover:text-amber-400 transition break-all inline-flex items-start gap-1.5"
          data-testid={`link-url-${link.id}`}
        >
          <span className="line-clamp-2">{short(link.url)}</span>
          <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0 opacity-60" />
        </a>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div>
          <div className="label-mono mb-1">Buyer</div>
          <div className="text-slate-200 font-medium truncate" data-testid={`buyer-name-${link.id}`}>
            {link.buyer_name || <span className="text-slate-600">—</span>}
          </div>
        </div>
        <div>
          <div className="label-mono mb-1">Vendor</div>
          <div className="text-slate-200 font-medium truncate" data-testid={`vendor-name-${link.id}`}>
            {link.vendor_name || <span className="text-slate-600">—</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-800">
        <div className="font-display font-black text-2xl text-emerald-400" data-testid={`price-amount-${link.id}`}>
          ${Number(link.price || 0).toFixed(2)}
        </div>

        <div className="flex items-center gap-1">
          <Select value={status} onValueChange={changeStatus} disabled={busy}>
            <SelectTrigger
              className="w-[160px] h-9 bg-slate-900/70 border-slate-800 text-slate-200 font-mono-vrax text-xs"
              data-testid={`status-select-${link.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0E131F] border-slate-800 text-slate-200">
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="ongoing">Ongoing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={copyUrl}
            className="btn-ghost-tactical p-2 rounded"
            title="Copy URL"
            data-testid={`copy-link-btn-${link.id}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onEdit(link)}
            className="btn-ghost-tactical p-2 rounded"
            title="Edit"
            data-testid={`edit-link-btn-${link.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={del}
            className="btn-ghost-tactical p-2 rounded hover:!border-red-500 hover:!text-red-400"
            title="Delete"
            data-testid={`delete-link-btn-${link.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
